import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { formatCurrency } from 'ddc-intl';
import { replaceContentWithJSX } from 'wsm-srp-utilities';
import { useRequestData, useLabels, useFlags } from 'wsm-common-data';

import Step from './Step';
import Payment from './Payment';
import StepMobile from './StepMobile';
import { stepsConfig } from '../constants';
import { initialState as stepsInitialState } from '../features/steps/stepsSlice';
import { setPaymentsCalcPencilingDataUpdate } from '../features/paymentsInfo/paymentsInfoSlice';
import { TEST_FUTURE_FLAG } from '../hooks/usePaymentData/constants';

const StepContainer = ({ hidden = false }) => {
	const { deviceType } = useRequestData();
	const flags = useFlags();
	const dispatch = useDispatch();
	const stepsInStore = useSelector((state) => state.steps);
	const [steps, setSteps] = useState(stepsInitialState);

	const isDesktop = deviceType === 'DESKTOP';
	const wrapperClasses = isDesktop ? 'd-flex flex-row' : 'd-flex flex-column';
	const containerPaddingClass = isDesktop ? '' : 'py-3';
	const labels = useLabels();

	const StepComponent = isDesktop ? Step : StepMobile;
	const stepKeys = Object.keys(stepsConfig);

	useEffect(() => {
		const pubsub = window?.DDC?.pubsub;
		if (pubsub) {
			pubsub.subscribe('ws-track-my-offer/step-tracker', (stepsInfo) =>
				setSteps(stepsInfo)
			);

			// Sync paymentsInfo from PaymentSelector widget instance
			pubsub.subscribe('ws-track-my-offer/paymentsInfo:updated', (data) => {
				if (flags[TEST_FUTURE_FLAG] && data) {
					dispatch(setPaymentsCalcPencilingDataUpdate(data));
				}
			});
		}

		return () => {
			if (pubsub) {
				pubsub.unsubscribe('ws-track-my-offer/step-tracker');
				pubsub.unsubscribe('ws-track-my-offer/paymentsInfo:updated');
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	const titleSideContent = (item, stepData = {}) => {
		if (
			item === 'payment' &&
			stepData?.complete &&
			stepData?.total &&
			stepData?.paymentType
		) {
			return (
				<Payment
					payment={formatCurrency(stepData.total)}
					hasTerm={stepData.paymentType !== 'cash'}
				/>
			);
		}
		return null;
	};

	const getTitle = (item, stepData = {}) => {
		if (
			item === 'payment' &&
			stepData?.complete &&
			stepData?.total &&
			stepData?.paymentType
		) {
			// ex: EST_FINANCE
			const paymentTypeLabel =
				stepsConfig[item].titleLabelPrefix +
				stepData.paymentType.toUpperCase();
			return labels.get(paymentTypeLabel);
		}
		if (item === 'tradeIn') {
			const tradeInLabel = labels.get('ADD_A_KBB_TRADE_IN_VALUE');
			// Minor: Using replaceContentWithJSX(label, ' ') on mobile may cause screen readers to read words without spacing
			const newTradeInLabel = isDesktop
				? replaceContentWithJSX(tradeInLabel, {
						'{{breakline}}': <br />
				  })
				: tradeInLabel.replace('{{breakline}}', ' ');
			return newTradeInLabel;
		}
		return labels.get(stepsConfig[item].titleLabel);
	};

	return (
		<div
			data-testid="step-container"
			style={hidden ? { display: 'none' } : undefined}
			className={`box box-padding border border-radius-md ${containerPaddingClass}`}
		>
			<div
				className={wrapperClasses}
				style={{ justifyContent: 'space-evenly' }}
			>
				{stepKeys
					.filter(
						(item) =>
							stepsInStore[item]?.enabled || item === 'payment'
					)
					.map((item, index, enabledSteps) => {
						// Safe check: Use initialState if step data pubsub event is missing
						const stepData =
							steps[item] || stepsInitialState[item] || {};

						return (
							<StepComponent
								key={item}
								isComplete={stepData.complete}
								title={getTitle(item, stepData)}
								route={stepsConfig[item].route}
								showStepDivider={
									enabledSteps.length - 1 !== index
								}
								titleSideContent={titleSideContent(
									item,
									stepData
								)}
								stepKey={item}
							/>
						);
					})}
			</div>
		</div>
	);
};

StepContainer.propTypes = {
	hidden: PropTypes.bool
};

export default StepContainer;
